import { Component, EventEmitter, Input, OnInit, Output, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';

interface CalendarDay {
  date: Date;
  dayNumber: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isPast: boolean;
  isInRange: boolean;
  isStart: boolean;
  isEnd: boolean;
}

@Component({
  selector: 'app-date-range-picker',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss']
})
export class DateRangePickerComponent implements OnInit {
  @Input() startDate: Date | null = null;
  @Input() endDate: Date | null = null;
  @Input() minDate: Date = new Date();
  @Input() isOpen: boolean = false;
  @Input() staticMode: boolean = false; // Takvimi inline/static olarak göster
  
  @Output() dateRangeSelected = new EventEmitter<{ startDate: Date; endDate: Date }>();
  @Output() closed = new EventEmitter<void>();

  // Internal state - parent'tan bağımsız
  start: Date | null = null;
  end: Date | null = null;
  hovered: Date | null = null;
  step: 'start' | 'end' = 'start';

  currentMonth: Date = new Date();
  calendarDays: CalendarDay[] = [];
  
  weekDays = ['Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct', 'Pz'];
  months = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

  constructor(private elementRef: ElementRef) {}

  ngOnInit(): void {
    this.currentMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    this.generateCalendar();
  }


  generateCalendar(): void {
    const days: CalendarDay[] = [];
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();
    
    // İlk günün haftanın hangi günü olduğunu bul (Pzt=0)
    const firstDay = new Date(year, month, 1);
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;
    
    // Önceki ayın günleri
    const prevMonth = new Date(year, month, 0);
    for (let i = startDay - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonth.getDate() - i);
      days.push(this.makeDay(date, false));
    }
    
    // Bu ayın günleri
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(this.makeDay(new Date(year, month, i), true));
    }
    
    // Sonraki ayın günleri
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push(this.makeDay(new Date(year, month + 1, i), false));
    }
    
    this.calendarDays = days;
  }

  private makeDay(date: Date, isCurrentMonth: boolean): CalendarDay {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const min = new Date(this.minDate);
    min.setHours(0, 0, 0, 0);

    return {
      date: new Date(date),
      dayNumber: date.getDate(),
      isCurrentMonth,
      isToday: d.getTime() === today.getTime(),
      isPast: d < min,
      isInRange: this.checkInRange(d),
      isStart: this.start ? this.norm(this.start) === d.getTime() : false,
      isEnd: this.end ? this.norm(this.end) === d.getTime() : false
    };
  }

  private norm(date: Date): number {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }

  private checkInRange(date: Date): boolean {
    const t = date.getTime();
    if (this.start && this.end) {
      return t > this.norm(this.start) && t < this.norm(this.end);
    }
    if (this.start && this.hovered && this.step === 'end') {
      const s = this.norm(this.start);
      const h = this.norm(this.hovered);
      if (h > s) return t > s && t < h;
      if (h < s) return t > h && t < s;
    }
    return false;
  }

  onDayClick(day: CalendarDay): void {
    if (day.isPast || !day.isCurrentMonth) return;
    
    const clicked = new Date(day.date);
    
    if (this.step === 'start') {
      this.start = clicked;
      this.end = null;
      this.step = 'end';
    } else {
      const s = this.norm(this.start!);
      const c = this.norm(clicked);
      
      if (c === s) {
        this.start = clicked;
        this.end = null;
        this.step = 'end';
      } else if (c < s) {
        this.end = new Date(this.start!);
        this.start = clicked;
        this.emitAndClose();
      } else {
        this.end = clicked;
        this.emitAndClose();
      }
    }
    
    this.generateCalendar();
  }

  private emitAndClose(): void {
    if (this.start && this.end) {
      this.dateRangeSelected.emit({
        startDate: this.start,
        endDate: this.end
      });
      this.step = 'start';
    }
  }

  onDayHover(day: CalendarDay): void {
    if (!day.isPast && day.isCurrentMonth && this.step === 'end') {
      this.hovered = new Date(day.date);
      this.generateCalendar();
    }
  }

  prevMonth(): void {
    const min = new Date(this.minDate.getFullYear(), this.minDate.getMonth(), 1);
    if (this.currentMonth > min) {
      this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
      this.generateCalendar();
    }
  }

  nextMonth(): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
    this.generateCalendar();
  }

  clear(): void {
    this.start = null;
    this.end = null;
    this.hovered = null;
    this.step = 'start';
    this.generateCalendar();
  }

  canGoPrev(): boolean {
    const min = new Date(this.minDate.getFullYear(), this.minDate.getMonth(), 1);
    return this.currentMonth > min;
  }

  getMonthYear(): string {
    return `${this.months[this.currentMonth.getMonth()]} ${this.currentMonth.getFullYear()}`;
  }

  getHint(): string {
    return this.step === 'start' ? 'Giriş tarihi seçin' : 'Çıkış tarihi seçin';
  }

  trackByDay(index: number, day: CalendarDay): string {
    return day.date.toISOString();
  }

  isStartDay(day: CalendarDay): boolean {
    return this.start ? this.norm(this.start) === this.norm(day.date) : false;
  }

  isEndDay(day: CalendarDay): boolean {
    return this.end ? this.norm(this.end) === this.norm(day.date) : false;
  }

  isInRange(day: CalendarDay): boolean {
    if (!day.isCurrentMonth) return false;
    const t = this.norm(day.date);
    if (this.start && this.end) {
      return t > this.norm(this.start) && t < this.norm(this.end);
    }
    if (this.start && this.hovered && this.step === 'end') {
      const s = this.norm(this.start);
      const h = this.norm(this.hovered);
      if (h > s) return t > s && t < h;
      if (h < s) return t > h && t < s;
    }
    return false;
  }
}
